import type { ShadowSettingsDto, ShadowType } from '../../dto/craft/props';
import { getAttribute } from '../parser';
import { parseInteger } from './value-parser';
interface ParsedShadow {
  shadowType: ShadowType;
  shadowSettings: ShadowSettingsDto;
}

const DEFAULT_SHADOW_SETTINGS: ShadowSettingsDto = {
  x: 0,
  y: 0,
  blur: 0,
  spread: 0,
  color: '#000000',
};

/**
 * Parse shadow attributes from an XML element.
 *
 * Expects individual attributes: shadowType, shadowX, shadowY, shadowBlur, shadowSpread, shadowColor.
 * Returns undefined if no shadow attributes are present (shadowType is not set).
 */
export const parseShadowAttributes = (element: Element): ParsedShadow | undefined => {
  const shadowTypeAttr = getAttribute(element, 'shadowType');
  if (!shadowTypeAttr) return undefined;

  const shadowType = mapShadowType(shadowTypeAttr);
  if (!shadowType) return undefined;

  if (shadowType === 'none') {
    return { shadowType: 'none', shadowSettings: { ...DEFAULT_SHADOW_SETTINGS } };
  }

  const x = parseInteger(getAttribute(element, 'shadowX'), 0);
  const y = parseInteger(getAttribute(element, 'shadowY'), 0);
  const blur = parseInteger(getAttribute(element, 'shadowBlur'), 0);
  const spread = parseInteger(getAttribute(element, 'shadowSpread'), 0);
  const colorAttr = getAttribute(element, 'shadowColor');
  const color = colorAttr || '#000000';

  return {
    shadowType: 'drop',
    shadowSettings: { x, y, blur, spread, color },
  };
};

const mapShadowType = (value: string): ShadowType | undefined => {
  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'drop':
      return 'drop';
    case 'none':
      return 'none';
    default:
      return undefined;
  }
};

/**
 * XML attribute names for shadow properties.
 * Used by ATTRIBUTES arrays and isValid* functions.
 */
export const SHADOW_ATTRIBUTES = [
  'shadowType',
  'shadowX',
  'shadowY',
  'shadowBlur',
  'shadowSpread',
  'shadowColor',
] as const;
